import * as React from "react";
import * as THREE from "three";
import {Viewer} from './js/main';
import {ContextMenu} from "./context-menu";
import {LayerSwitcher} from "./layer-switcher";
import {useEffect, useRef, useState} from "react";
import {createStyles, makeStyles, Theme, useTheme} from "@material-ui/core/styles";
import {changeMeasureUnits, selectData, selectView} from '../redux/projections-slice';
import {useSelector, useDispatch} from 'react-redux';
import {
  getCameraFileAssets,
  getPointCloudAssets,
  getOrthoAssets,
  selectAllAssets,
  getTagAssets,
  getModelAssets
} from "../redux/assets-slice";
import {headerHeight, toast} from "../app";
import LocalScene from "./js/projections";
import {
  selectName,
  selectProjectModified
} from "../redux/project-slice";
import {assetDrawerWidth, assetDrawerHeight} from "../asset-drawer";
import {Button} from "@material-ui/core";
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import {changeAvailableClassifications, changeSwitched,
  selectViewerSwitched} from "../redux/settings-slice";
import { CursorTooltip } from "../tooltips";
import { AlertDialog } from "../dialogs";
import {isDevMode, isStaticSite} from '../electron-modules';
import {
  changeCameraState,
  changeSavedAerialState,
  initialAerialState,
  initialSceneState,
  selectBasicAdjustments,
  selectCameraHeight,
  selectCameraState,
  selectCameraTransform,
  selectSavedAerialState
} from "../redux/camera-slice";
import { isEqual } from 'lodash';
import { readTransformFile } from "../camera-utils";
import proj4 from 'proj4';
import { transform } from 'ol/proj';
import {register} from 'ol/proj/proj4';
import {
  getModifiedAerialState,
  getModifiedSceneState,
  parseURLParams,
  roundDigit,
  toBoolean
} from "./js/utilities";
import { MathUtils } from "three";
import { clearWindowHash, setWindowHash } from "../project-utils";
import { sendCustomEvent } from "../events";
import { throttle } from 'throttle-debounce';

export const viewerBackground = 'linear-gradient(180deg, #fdfcfb 0%, #e2d1c3 100%)';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    parent: {
      width: "100vw",
      height: `calc(100vh - ${headerHeight}px)`,
    },
    containerLarge: {
      width: '100%',
      height: `calc(100vh - ${headerHeight}px)`,
      position: 'fixed',
      "@media (max-width: 600px) and (min-height: 450px)": {
        /* Mobile devices (portrait) */
        marginTop: "25vh",
        height: `calc(100vh - 25vh - ${headerHeight}px)`,
      },
      "@media (max-height: 450px)": {
        /* Mobile devices (landscape) */
        marginLeft: "30vw",
        width: "calc(100% - 30vw)"
      },
    },
    offsetLarge: {
      marginTop: theme.spacing(0),
      marginLeft: assetDrawerWidth,
      width: `calc(100% - ${assetDrawerWidth}px)`,
      height: `calc(100vh - ${headerHeight}px)`,
    },
    containerSmall: {
      width: `${assetDrawerWidth}px`,
      height: `${assetDrawerWidth*0.75}px`,
      position: 'fixed',
      background: 'white',
      margin: '0.5em',
      boxShadow: "0px 0px 1px 1px rgba(0,0,0,0.5)",
      zIndex: 1,
      "@media (max-width: 600px) and (min-height: 450px)": {
        /* Mobile devices (portrait) */
        height: `25vh`,
        width: "100%",
        margin: theme.spacing(0),
      },
      "@media (max-height: 450px)": {
        /* Mobile devices (landscape) */
        width: "30%",
        height: `calc(100% - ${headerHeight}px)`,
        margin: theme.spacing(0)
      },
    },
    offsetSmall: {
      width: `${assetDrawerWidth}px`,
      height: `${100-assetDrawerHeight}%`,
      bottom: theme.spacing(0),
      margin: theme.spacing(0),
      border: theme.spacing(0),
      borderTop: "1px solid black",
      boxShadow: "none",
      zIndex: theme.zIndex.modal,
    },
    compassElement: {
      pointerEvents: "none",
      position: "absolute",
      top: theme.spacing(0),
      right: theme.spacing(0)
    },
    gradient: {
      backgroundImage: viewerBackground
    },
    buttonParent: {
      padding: "3px",
      background: "rgba(255,255,255,0.4)",
      position: 'absolute',
      top: "0.5em",
      left: "0.5em",
      zIndex: 10,
      borderRadius: "5px",
      "-webkit-transition": "-webkit-transform .3s ease-in-out",
      transition: "transform .3s ease-in-out",
      "&:hover": {
        "-webkit-transform": "rotate(-180deg)",
        transform: "rotate(-180deg)"
      }
    },
    button: {
      padding: "0px !important",
      background: "rgba(0,60,136,0.5)",
      color: "white",
      minWidth: "24px !important",
      "&:hover": {
        background: "rgba(0,60,136,0.7)",
      }
    }
  }),
);

const ViewSwitcher = props => {
  const {classes, visible, handleSwitched} = props;

  return (
    <React.Fragment>
      {visible && (<div className={classes.buttonParent}>
        <Button
          className={classes.button}
          onClick={handleSwitched}
        >
          <RotateLeftIcon/>
        </Button>
      </div>)}
    </React.Fragment>
  );
};

export const SceneViewer = (props) => {
  const viewer = props.viewer as Viewer;
  const {setViewer, assetDrawerState, setAddObservations,
    setMeasurements, resetObservationType, setSingleAlignment,
    orthoLabelOpen, setOrthoLabelOpen, setCategoryList, setTrainingArea,
    closeAllTools, setPointMarkupOperations, setMultiImageOpen,
    setMultiImageState} = props;

  const theme = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();

  const viewerRef = useRef(null);
  const aerialRef = useRef(null);

  const assets = useSelector(selectAllAssets);

  const dataProjection = useSelector(selectData);
  const viewProjection = useSelector(selectView);
  const cameraTransformPath = useSelector(selectCameraTransform);
  const projectModifiedDate = useSelector(selectProjectModified);
  const projectName = useSelector(selectName);
  const viewerSwitched = useSelector(selectViewerSwitched);
  const cameraHeight = useSelector(selectCameraHeight);
  const basicAdjustments = useSelector(selectBasicAdjustments);
  const savedAerialState = useSelector(selectSavedAerialState);
  const savedCameraState = useSelector(selectCameraState);

  const [viewerTooltipLines, setViewerTooltipLines] = useState([]);
  const [aerialTooltipLines, setAerialTooltipLines] = useState([]);
  const [customAlertHTML, setCustomAlertHTML] = useState(null);
  const [aerialState, setAerialState] = useState({...initialAerialState});
  const [cameraState, setCameraState] = useState({...initialSceneState});

  const clearTooltipText = () => {
    clearViewerTooltip();
    clearAerialTooltip();
  };

  const getClasses = (switched) => {
    let mapClasses = [];
    let viewerClasses = [classes.gradient];

    const classLarge = [
      classes.containerLarge,
      assetDrawerState ? classes.offsetLarge : ""
    ];
    const classSmall = [
      classes.containerSmall,
      assetDrawerState ? classes.offsetSmall : ""
    ];

    if (switched) {
      mapClasses = [...mapClasses, ...classLarge];
      viewerClasses = [...viewerClasses, ...classSmall];
    } else {
      mapClasses = [...mapClasses, ...classSmall];
      viewerClasses = [...viewerClasses, ...classLarge];
    }

    return {
      viewerClasses: viewerClasses.join(" "),
      mapClasses: mapClasses.join(" ")
    };
  };

  const handleSwitched = () => {
    dispatch(changeSwitched(!viewerSwitched));
  };

  const clearAllAssets = () => {
    // Adding an empty array will clear everything
    addAllAssets([]);
  };

  const addAllAssets = (assetList) => {
    const viewerAssets = {
      camerasFiles: getCameraFileAssets(assetList),
      pointClouds: getPointCloudAssets(assetList),
      orthoFiles: getOrthoAssets(assetList),
      tagFiles: getTagAssets(assetList),
      modelFiles: getModelAssets(assetList)
    };

    viewer?.reconcileAssets(
      viewerAssets,
      cameraHeight,
      basicAdjustments,
      savedCameraState
    );

    viewer?.checkShiftReset();
  };

  const handleHashUpdate = () => {
    if (!isStaticSite) {
      return;
    }

    const state = {} as any;

    /** Scene state values */
    if (cameraState.camera) {
      const [theta, phi] = cameraState.angles;
      state.cam = cameraState.camera;
      state.fov = cameraState.fov;
      state.theta = roundDigit(MathUtils.radToDeg(theta), 3);
      state.phi = roundDigit(MathUtils.radToDeg(phi), 3);
    }

    if (cameraState.orbit) {
      const [ox, oy, oz] = cameraState.orbit;
      state.ox = roundDigit(ox, 3);
      state.oy = roundDigit(oy, 3);
      state.oz = roundDigit(oz, 3);

      const [px, py, pz] = cameraState.pivot;
      state.px = roundDigit(px, 3);
      state.py = roundDigit(py, 3);
      state.pz = roundDigit(pz, 3);
    }

    /** Aerial state values */
    if (aerialState.center) {
      const [lon, lat] = transform(aerialState.center, 'EPSG:3857', 'EPSG:4326');
      state.lat = roundDigit(lat, 8);
      state.lon = roundDigit(lon, 8);
      state.zoom = roundDigit(aerialState.zoom, 3);
      state.rotation = roundDigit(aerialState.rotation, 3);
      state.expanded = viewerSwitched ? 1 : 0;
    }

    const hash = Object.entries(state)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    if (cameraState.orbit || cameraState.camera) {
      setWindowHash(hash);
    } else {
      clearWindowHash();
    }
  };

  const handleHashChange = (url) => {
    if (!isStaticSite) {
      return;
    }

    const parameters = parseURLParams(url);
    if (!parameters) {
      return;
    }

    const modifiedSceneState = getModifiedSceneState(
      viewer.sceneState, parameters);

    const modifiedAerialState = getModifiedAerialState(
      viewer.aerialState, parameters);

    if ("expanded" in parameters) {
      const switched = toBoolean(parameters.expanded);
      dispatch(changeSwitched(switched));
    }

    viewer.resetDefaultAerialState(modifiedAerialState);
    viewer.resetDefaultSceneState(modifiedSceneState);
  };

  useEffect(() => {
    /** Ran after new project is created or project is loaded */
    closeAllTools();
    viewer?.resetDefaultAerialState(savedAerialState);
    viewer?.resetDefaultAerialCamera();
    viewer?.resetDefaultControlState();
  }, [projectModifiedDate]);

  useEffect(() => {
    if (!viewer) return;

    handleHashUpdate();
    dispatch(changeCameraState(cameraState));
  }, [viewer, cameraState]);

  useEffect(() => {
    if (!viewer) return;

    handleHashUpdate();
    dispatch(changeSavedAerialState(aerialState));
  }, [viewer, aerialState]);

  useEffect(() => {
    if (!viewer) return;

    viewer.setProjectName(projectName);
  }, [viewer, projectName]);

  useEffect(() => {
    if (!viewer) return;

    clearTooltipText();
    if (viewerSwitched) {
      viewer.removeInteractions();
    }

    handleHashUpdate();
    viewer.setExpanded(!viewerSwitched);
  }, [viewer, viewerSwitched]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (viewer) {
        const classifications = viewer.getAvailableClassifications();
        dispatch(changeAvailableClassifications(classifications));
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [viewer, dispatch]);

  useEffect(() => {
    window.onhashchange = event => {
      handleHashChange(event.newURL);
    };
  }, [viewer]);

  const compassClassName = classes.compassElement;

  useEffect(() => {
    const viewerProps = {
      setMeasurements,
      setAerialState,
      setSingleAlignment,
      setAddObservations,
      resetObservationType,
      setCameraState,
      orthoLabelOpen,
      setOrthoLabelOpen,
      setCategoryList,
      setTrainingArea,
      setPointMarkupOperations,
      setMultiImageOpen,
      setMultiImageState,
      compassClassName
    };

    let viewerObj = new Viewer(
      viewerRef.current,
      aerialRef.current,
      viewerProps
    );

    setViewer(viewerObj);

    if (isDevMode) {
      // Temporary global for easy access
      window['viewer'] = viewerObj;
      window['local'] = LocalScene;
      window['THREE'] = THREE;
    }

    return () => {
      console.log("Destroy viewer");
      viewerObj.destroy();
    };
  }, []);

  useEffect(() => {
    // Register current projection for openlayers to use
    proj4.defs(dataProjection.name, dataProjection.string);
    register(proj4);

    // Clear previous asset data
    clearAllAssets();

    // Update projection
    viewer?.setDataProjection(dataProjection);

    // Force assets to reload
    addAllAssets(assets);
  }, [viewer, dataProjection]);

  useEffect(() => {
    viewer?.setViewProjection(viewProjection);

    const units = LocalScene.viewProjectionUnits;
    dispatch(changeMeasureUnits(units));
  }, [viewer, viewProjection]);

  useEffect(() => {
    if (cameraTransformPath === null) {
      viewer?.resetCameraMatrix();
      return;
    }

    readTransformFile(cameraTransformPath)
      .then(data => {
        const {success, content} = data;

        if (success) {
          viewer?.setCameraMatrix(content);
        } else {
          toast.error(content);
        }
      });
  }, [viewer, cameraTransformPath]);

  useEffect(() => {
    viewer?.setCameraHeights(cameraHeight);
  }, [viewer, cameraHeight]);

  useEffect(() => {
    viewer?.setCameraAdjustments(basicAdjustments);
  }, [viewer, basicAdjustments]);

  useEffect(() => {
    addAllAssets(assets);
  }, [viewer, assets]);

  /** Viewer tooltip event listener */
  useEffect(() => {
    const callback = throttle(50, (event) => {
      const {lines} = event.detail;

      // NOTE: equality is manually checked, because
      // react will always return true for two arrays
      if (isEqual(lines, viewerTooltipLines)) return;
      setViewerTooltipLines(lines);
    });

    document.addEventListener("viewer-tooltip", callback);

    return () => {
      document.removeEventListener("viewer-tooltip", callback);
    };
  }, [viewerTooltipLines]);

  /** Aerial tooltip event listener */
  useEffect(() => {
    const callback = throttle(50, (event) => {
      const {lines} = event.detail;

      // NOTE: equality is manually checked, because
      // react will always return true for two arrays
      if (isEqual(lines, aerialTooltipLines)) return;
      setAerialTooltipLines(lines);
    });

    document.addEventListener("aerial-tooltip", callback);

    return () => {
      document.removeEventListener("aerial-tooltip", callback);
    };
  }, [aerialTooltipLines]);

  let {viewerClasses, mapClasses} = getClasses(viewerSwitched);

  return (
    <React.Fragment>
      <div className={classes.parent}>

        {/* Viewer div */}
        <CursorTooltip textLines={viewerTooltipLines}>
          <div
            ref={viewerRef}
            className={viewerClasses}
            onMouseLeave={() => {
              clearViewerTooltip();
            }}
          >
            <ViewSwitcher
              classes={classes}
              visible={viewerSwitched}
              handleSwitched={handleSwitched}
            />

            <ContextMenu
              viewer={viewer}
              clearTooltipText={clearTooltipText}
            />
          </div>
        </CursorTooltip>

        {/* Map div */}
        <CursorTooltip textLines={aerialTooltipLines}>
          <div
            ref={aerialRef}
            className={mapClasses}
            onMouseLeave={() => {
              clearAerialTooltip();
            }}
          >
            <ViewSwitcher
              classes={classes}
              visible={!viewerSwitched}
              handleSwitched={handleSwitched}
            />
            <LayerSwitcher
              viewer={viewer}
              clearTooltipText={clearTooltipText}
            />
          </div>
        </CursorTooltip>
      </div>

      {/* Viewer alert dialog */}
      <AlertDialog
        open={customAlertHTML !== null}
        title="Alert Message"
        handleClose={() => {
          setCustomAlertHTML(null);
        }}
      >
        <div dangerouslySetInnerHTML={{__html: customAlertHTML}}/>
      </AlertDialog>
    </React.Fragment>
  );
};

export const clearAerialTooltip = () => setAerialTooltip([]);
export const clearViewerTooltip = () => setViewerTooltip([]);

export const setAerialTooltip = (lines: string[]) => {
  sendCustomEvent("aerial-tooltip", {lines});
};

export const setViewerTooltip = (lines: string[]) => {
  sendCustomEvent("viewer-tooltip", {lines});
};
